import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import GanttChart from "./GanttChart";
import GanttFinanceiroProjeto from "./GanttFinanceiroProjeto";
import ModalSolicitarAditivo from "./ModalSolicitarAditivo";
import ModalAuditoria from "./ModalAuditoria";
import "../css/dashboard.css";

import { getUserRole, getUserSecretariaId, fetchToken } from "./Auth";
import ModalRelatorios from "./viewrelatorios";
import ModalDocumentos from "./modalDocs";

function ViewProjetoCompleto() {
  const { id } = useParams();
  const [projeto, setProjeto] = useState(null);

  const [cronogramaData, setCronogramaData] = useState([]);

  const [valorGasto, setValorGasto] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);

  const [showDocumentosModal, setShowDocumentosModal] = useState(false);

  const userRole = getUserRole();
  const userSecretariaId = getUserSecretariaId();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchDadosCompletos = async () => {
      const token = fetchToken();
      if (!token) {
        setError("Sessão inválida. Faça login novamente.");
        setLoading(false);
        return;
      }

      try {
        const resProjeto = await fetch(
          `/api/projetocompleto/${id}`
        );
        const dataProjeto = await resProjeto.json();

        if (dataProjeto.Status === "Success" && dataProjeto.Result.length > 0) {
          const p = dataProjeto.Result[0];
          setProjeto(p);

          try {
            const resCronograma = await axios.get(
              `/api/cronograma/${id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            const cronogramaItens = resCronograma.data || [];
            setCronogramaData(cronogramaItens);

            const somaTotalGasto = cronogramaItens.reduce((accItens, item) => {
              const parcelas = item.mensal || [];
              const somaParcelasDoItem = parcelas.reduce(
                (accParcelas, parcela) => {
                  return accParcelas + parseFloat(parcela.valor_simples || 0);
                },
                0
              );
              return accItens + somaParcelasDoItem;
            }, 0);

            setValorGasto(somaTotalGasto);
          } catch (cronogramaErr) {
            console.error("Erro ao buscar cronograma:", cronogramaErr.message);
            setValorGasto(0);
            setCronogramaData([]);
          }
        } else {
          setError("Projeto não encontrado");
        }
      } catch (err) {
        setError("Erro ao carregar dados do projeto");
        console.error(err);
      } finally {
        setLoading(false);
      }

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setUser(payload);
        } catch (e) {
          console.error("Erro ao decodificar o token:", e);
        }
      }
    };

    fetchDadosCompletos();
  }, [id]);

  const calcularProgressoEtapa = (inicio, fim, situacao) => {
    if (situacao === "Concluído") return 100;
    if (situacao === "Não iniciado") return 0;
    if (!inicio || !fim) return 0;

    const hoje = new Date();
    const dataInicio = new Date(`${inicio}T00:00:00`);
    const dataFim = new Date(`${fim}T23:59:59`);

    if (situacao === "Em andamento") {
      if (hoje < dataInicio) return 0;
      if (hoje > dataFim) return 100;

      const totalDias = (dataFim - dataInicio) / (1000 * 60 * 60 * 24);
      const diasPassados = (hoje - dataInicio) / (1000 * 60 * 60 * 24);

      if (totalDias <= 0) return 100;

      return Math.round((diasPassados / totalDias) * 100);
    }
    return 0;
  };

  const progresso = () => {
    if (!projeto) return 0;
    const etapas = [1, 2, 3, 4, 5, 6];
    const soma = etapas.reduce((acc, n) => {
      return (
        acc +
        calcularProgressoEtapa(
          projeto[`data_inicio_etapa${n}`],
          projeto[`data_termino_etapa${n}`],
          projeto[`situacao_etapa${n}`]
        )
      );
    }, 0);
    return Math.round(soma / etapas.length);
  };

  const etapasAtrasadas = () => {
    if (!projeto) return [];
    const hoje = new Date();
    return [1, 2, 3, 4, 5, 6]
      .filter((n) => {
        const fim = projeto[`data_termino_etapa${n}`];
        const situacao = projeto[`situacao_etapa${n}`];
        const dataFim = new Date(`${fim}T23:59:59`);
        return situacao === "Em andamento" && hoje > dataFim;
      })
      .map((n) => ({
        etapa: projeto[`etapa${n}`] || `Etapa ${n}`,
        numero: n,
      }));
  };

  const restante = () =>
    Math.max(0, (parseFloat(projeto?.valor_total_projeto) || 0) - valorGasto);

  const percentualGasto = () => {
    const total = parseFloat(projeto?.valor_total_projeto) || 0;
    return total ? Math.round((valorGasto / total) * 100) : 0;
  };

  const statusFinal = (prog) => {
    const base =
      prog === 0 ? "Não iniciado" : prog < 100 ? "Em andamento" : "Concluído";
    const limite = new Date(`${projeto?.data_termino_etapa6}T23:59:59`);
    const hoje = new Date();
    return hoje > limite && prog < 100 ? "Atrasado" : base;
  };

  const progAtual = progresso();
  const atrasadas = etapasAtrasadas();
  const statusAtual = statusFinal(progAtual);

  const corProgresso =
    atrasadas.length > 0
      ? "bg-danger"
      : progAtual === 100
      ? "bg-success"
      : "bg-primary";

  const corStatus =
    atrasadas.length > 0
      ? "text-danger"
      : progAtual === 100
      ? "text-success"
      : "text-primary";

  const handleGerarPDF = async () => {
    try {
      const response = await fetch("/api/gerar-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [id] }),
      });

      if (!response.ok) throw new Error("Erro ao gerar PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "relatorio_projetos.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      alert("Erro ao gerar o PDF do projeto");
    }
  };

  if (loading)
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border text-primary" role="status" />
        <h3 className="mt-3">Carregando dashboard...</h3>
      </div>
    );

  if (error || !projeto)
    return (
      <div className="container mt-4 alert alert-danger">
        <h3>{error || "Projeto não pôde ser carregado."}</h3>
      </div>
    );

  return (
    <div className="container-fluid mt-4" id="dashboard-content">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <h2 className={`fw-bold mb-0 ${corStatus}`}>{projeto.nomeprojeto}</h2>
        <div>
          <Link to="/projetocompleto" className="btn back-btn me-2">
            <i className="fas fa-arrow-left" /> Voltar
          </Link>
          <button
            className="btn btn-outline-primary my-2"
            onClick={handleGerarPDF}
          >
            <i className="fas fa-file-pdf" /> Gerar PDF
          </button>
          <Link
            className="btn btn-with btn-lg fw-bold ms-2 me-2 mb-2"
            onClick={() => setShowDocumentosModal(true)}
          >
            <i className="fas fa-folder-open me-1"></i> Ver Documentos
          </Link>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-start flex-wrap">
          <Link
            to={`/detalhesexecucao/${projeto.id}`}
            className="btn btn-info btn-lg fw-bold me-2 mb-2"
          >
            Detalhes da Execução
          </Link>
          <Link
            to={`/estatisticasprojeto/${projeto.id}`}
            className="btn btn-primary btn-lg fw-bold me-2 mb-2"
          >
            Estatísticas do Projeto
          </Link>

          <Link
            to="#"
            className="btn btn-secondary btn-lg fw-bold ms-2 me-2 mb-2"
            onClick={() => setShowRelatorioModal(true)}
          >
            Ver Relatórios
          </Link>

          {showRelatorioModal && (
            <ModalRelatorios
              projetoId={projeto.id}
              onClose={() => setShowRelatorioModal(false)}
            />
          )}

          <Link
            to={`/relatorio/${projeto.id}`}
            className="btn btn-danger btn-lg fw-bold ms-2 me-2 mb-2"
          >
            Inserir Relatório
          </Link>

          {(() => {
            if (userRole === "auditor") {
              return (
                <>
                  {showModal && (
                    <ModalAuditoria
                      projetoId={projeto.id}
                      onClose={() => setShowModal(false)}
                    />
                  )}
                  <Link
                    to="#"
                    className="btn btn-warning btn-lg fw-bold mb-2"
                    onClick={() => setShowModal(true)}
                  >
                    Abrir Auditoria
                  </Link>
                </>
              );
            } else if (userSecretariaId === projeto?.id_secretaria) {
              return (
                <>
                  <button
                    className="btn btn-warning ms-2 mb-2"
                    onClick={() => setShowModal(true)}
                  >
                    Solicitar Aditivo
                  </button>
                  {showModal && (
                    <ModalSolicitarAditivo
                      projetoId={projeto.id}
                      idSecretaria={projeto.id_secretaria}
                      onClose={() => setShowModal(false)}
                    />
                  )}
                </>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {atrasadas.length > 0 && (
        <div className="alert alert-danger">
          <strong>Etapas atrasadas:</strong>{" "}
          {atrasadas.map((e) => (
            <span key={e.numero} className="me-2">
              {e.etapa}
            </span>
          ))}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-4">
          <div className="card dashboard-card">
            <div className="card-body">
              <h5 className="card-title">Projeto</h5>
              <p>
                <strong>Secretaria:</strong> {projeto.secretaria_nome}
              </p>
              <p>
                <strong>Responsável:</strong> {projeto.gestor}
              </p>
              <p>
                <strong>Data Limite:</strong>{" "}
                {projeto.data_termino_etapa6
                  ? new Date(projeto.data_termino_etapa6).toLocaleDateString(
                      "pt-BR"
                    )
                  : "Não definida"}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card dashboard-card">
            <div className="card-body">
              <h5 className="card-title">Status</h5>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className={`badge ${corStatus}`}>{statusAtual}</span>
                <span className="value-display">{progAtual}%</span>
              </div>
              <div className="progress">
                <div
                  className={`progress-bar ${corProgresso}`}
                  style={{ width: `${progAtual}%` }}
                />
              </div>
              <small className="text-muted">
                {statusAtual === "Atrasado"
                  ? "Projeto em atraso"
                  : `${progAtual}% concluído`}
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card dashboard-card">
            <div className="card-body">
              <h5 className="card-title">Orçamento</h5>
              <p>
                <strong>Orçado (Total):</strong> R${" "}
                {parseFloat(projeto.valor_total_projeto || 0).toLocaleString(
                  "pt-BR",
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </p>
              <p>
                <strong>Gasto:</strong> R${" "}
                {valorGasto.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </p>
              <p>
                <strong>Restante:</strong> R${" "}
                {restante().toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </p>
              <div className="progress">
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${percentualGasto()}%` }}
                />
              </div>
              <small className="text-muted">
                {percentualGasto()}% utilizado
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12">
          {!loading && cronogramaData.length > 0 ? (
            <GanttChart cronograma={cronogramaData} />
          ) : (
            !loading && (
              <div className="alert alert-light text-center">
                (Sem dados de cronograma para exibir o gráfico físico)
              </div>
            )
          )}
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12">
          <GanttFinanceiroProjeto projeto={projeto} valorGasto={valorGasto} />
        </div>
      </div>

      {showDocumentosModal && (
        <ModalDocumentos
          projetoId={projeto.id}
          onClose={() => setShowDocumentosModal(false)}
        />
      )}
    </div>
  );
}

export default ViewProjetoCompleto;
